
import styles from "../RightSidebar/rightsidebar.module.css";
import PrimaryButton from "../Buttons/PrimaryButton";
import { appendExpandedTimeLabel, formatDateDayMonth, getInitials, getConsistentColor, removedSeconds, scrollToBookingCard, scrollToNewBookingCard, ensureDateObject, normalizeDateInput } from "../../Utils/utils";
import Guesticon from "../../Assets/images/guestIcon.svg";
import DropdownImageTrigger from "../DropDown/DropdownImageTrigger";
import menuIcon from "../../Assets/images/menu.svg";
import { ImageLink } from "../Buttons/ImageLink";
import editIcon from "../../Assets/images/EditIcon.svg";
import { useGlobalContext } from "../../Context/GlobalContext";
import BookingDetails from "../BookingDetails/BookingDetails";
import BookingInfoLoader from "./BookingInfoLoader/BookingInfoLoader";
import PrimiumMemberIcon from "../Icons/PrimiumMemberIcon";
import { RequestedTherapist } from "../Badges/RequestedTherapist";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import RepeatBookingIcon from "../Icons/RepeatBookingIcon";
import DropdownRepeatBookingDates from "../DropDown/DropdownRepeatBookingDates";
import { RequestedRoom } from "../Badges/RequestedRoom";
import { getRepeatBookingList } from "../../APICalling/APICalling";
import BookingNotes from "../BookingNotes/BookingNotes";
import logger from "../../Utils/logger";
import { findLiveBookingListEntry, buildLiveGuestTagMap, mergeLiveGuestTagIntoItem, resolvePrimaryCustomerBookings, isPrimaryCustomerGroup } from "../../Utils/liveGuestCheckinTag";
import { getBookingPreventChanges } from "../../Utils/BookingPreventChanges";

const BookingInfo = ({
	onCheckout,
	onShowCancelPopup,
	onShowCancelWithRefundPopup,
	onEdit,
	setCurrentStep,
	onViewSale,
	handleStatus,
	confirmedAgainPopup,
	showAddNotePopup,
	saleNote,
	showRepeatPopup
}) => {
	const location = useLocation();
	const lastScrollNavKeyRef = useRef(null);


	const { 
		sidebarLoading, 
		bookingsList, 
		selectedBookingDate, 
		setSelectedBookingDate, 
		selectedBookingId,
		setSelectedBookingId, 
		setShowBookingSummary, 
		setIsSidebarVisible,
		selectedDate,
		setSelectedDate,
		setSidebarLoading,
		bookingDetails,
		customerBookingItems,
		isViewOnlyOutlet,
		selectedOutletData,
	} = useGlobalContext();

	const isUnder12CheckinExempt = (booking) => (
		(
			booking?.checkin_required === false ||
			booking?.checkin_required === 0 ||
			booking?.checkin_required === "0" ||
			booking?.checkin_required === "false"
		) &&
		booking?.checkin_exempt_reason === "under_12"
	);

	const getGuestCheckinTagLabel = (booking) => {
		if (Number(booking?.below_12) === 1 || isUnder12CheckinExempt(booking)) {
			return "Below 12";
		}
		if (booking?.guest_checkin_id) {
			return `${booking?.guest_first_name || ""} ${booking?.guest_last_name || ""}`.trim();
		}
		return "";
	};
	const currentBooking = findLiveBookingListEntry(bookingsList, bookingDetails?.id);
	const liveGuestTagFieldsById = buildLiveGuestTagMap(currentBooking?.booking_item);

	const withLiveGuestTag = (booking) => mergeLiveGuestTagIntoItem(booking, liveGuestTagFieldsById);

	// Guest check-in data for the primary customer: user.name, then primary===1 item, then first booking_item key
	const customerCheckedInData = (() => {
		const bookings = resolvePrimaryCustomerBookings(customerBookingItems, bookingDetails?.user?.name);
		if (!Array.isArray(bookings) || bookings.length === 0) return null;
		const first = withLiveGuestTag(bookings[0]);
		return {
			guest_checkin_id: first?.guest_checkin_id ?? null,
			guest_first_name: first?.guest_first_name ?? null,
			guest_last_name: first?.guest_last_name ?? null,
			tagLabel: getGuestCheckinTagLabel(first),
		};
	})();

	useEffect(() => {
		if (selectedBookingDate !== bookingDetails?.service_date) {
			setSelectedBookingDate(bookingDetails?.service_date);
		}
	}, [selectedBookingDate, bookingDetails?.service_date, setSelectedBookingDate]);

	const repeatBooking = bookingDetails?.recurring_group_id !== null && bookingDetails?.recurring_group_id !== undefined && bookingDetails?.recurring_group_id !== '';

	const rawStatus = currentBooking?.status?.toLowerCase() || bookingDetails?.status?.toLowerCase() || '';

	const isCustomerDeleted = currentBooking?.user === null || false;

	// Normalize equivalent backend status values to one canonical status.
	const normalizedStatus = rawStatus === 'checkedin'
		? 'checked-in'
		: rawStatus === 'closed'
			? 'completed'
			: rawStatus;
	const status = normalizedStatus;

	const statusLabel = normalizedStatus === 'voided' ? 'Refunded' : status;

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isStatusSubmitting, setIsStatusSubmitting] = useState(false);
	const [isViewSaleLoading, setIsViewSaleLoading] = useState(false);
	const [repeatBookingDates, setRepeatBookingDates] = useState([]);
	const [repeatBookingDatesLoading, setRepeatBookingDatesLoading] = useState(false);
	const [repeatBookingEmptyMessage, setRepeatBookingEmptyMessage] = useState(null);
	const [expandedCommissions, setExpandedCommissions] = useState(new Set());

	const toggleCommission = (bookingKey) => {
		setExpandedCommissions((prev) => {
			const next = new Set(prev);
			if (next.has(bookingKey)) next.delete(bookingKey);
			else next.add(bookingKey);
			return next;
		});
	};

	const handleRepeatDropdownOpen = async () => {
		const recurringGroupId = bookingDetails?.recurring_group_id;
		if (!recurringGroupId) return;
		setRepeatBookingDatesLoading(true);
		setRepeatBookingEmptyMessage(null);
		try {
			const response = await getRepeatBookingList(recurringGroupId);
			if (response?.message || response?.isAxiosError) {
				setRepeatBookingDates([]);
				return;
			}
			const rawList = response?.data?.data?.bookings ?? (Array.isArray(response) ? response : []);
			const dates = (Array.isArray(rawList) ? rawList : []).map((item) => {
				const date = item?.service_date;
				const bookingId = item?.id || item?.booking_id;
				const bookingItemId = item?.booking_item_id;
				return {
					date: date,
					booking_id: bookingId,
					booking_item_id: bookingItemId,
				};
			}).filter((d) => d.date && (d.booking_id || d.booking_item_id));
			setRepeatBookingDates(dates);
			if (dates.length === 0 && (response?.data?.message || response?.data?.data?.message)) {
				setRepeatBookingEmptyMessage(response?.data?.message || response?.data?.data?.message);
			} else {
				setRepeatBookingEmptyMessage(null);
			}
		} catch (err) {
			setRepeatBookingDates([]);
			setRepeatBookingEmptyMessage(null);
		} finally {
			setRepeatBookingDatesLoading(false);
		}
	};

	const handleRepeatDateClick = (dateObj) => {
		if (!dateObj?.date || (!dateObj?.booking_id && !dateObj?.booking_item_id)) return;
		
		// Parse DD-MM-YYYY format to Date object
		const bookingDate = ensureDateObject(dateObj?.date);
		// Validate the date before using it
		if (isNaN(bookingDate.getTime())) {
			console.error("Invalid date format:", dateObj?.date);
			return;
		}
		sessionStorage.setItem("skipCurrentTimeAutoScroll", "1");
		
		setSelectedDate(bookingDate);
		setIsSidebarVisible(true);
		setShowBookingSummary(true);
		setSelectedBookingId(dateObj?.booking_id);
		setSidebarLoading(true);
		setTimeout(() => {
			if (dateObj?.booking_id) {
				scrollToBookingCard(dateObj.booking_item_id);
			}
		}, 100);
	};

	// Resolve the actual calendar date a service item belongs to (handles cross-day split items).
	const getBookingItemDateObject = (booking) => {
		const rawDate = booking?.start_date || booking?.service_date;
		const normalizedFromDate = rawDate ? normalizeDateInput(rawDate) : null;
		if (normalizedFromDate && !isNaN(normalizedFromDate.getTime())) return normalizedFromDate;

		if (typeof booking?.service_at === "string" && booking.service_at.includes(" ")) {
			const [datePart] = booking.service_at.split(" ");
			const normalizedFromServiceAt = normalizeDateInput(datePart);
			if (normalizedFromServiceAt && !isNaN(normalizedFromServiceAt.getTime())) return normalizedFromServiceAt;
		}
		return null;
	};

	const isSameCalendarDay = (dateA, dateB) => (
		dateA instanceof Date && !isNaN(dateA.getTime()) &&
		dateB instanceof Date && !isNaN(dateB.getTime()) &&
		dateA.getFullYear() === dateB.getFullYear() &&
		dateA.getMonth() === dateB.getMonth() &&
		dateA.getDate() === dateB.getDate()
	);

	// If the clicked service lives on a different calendar date than the one currently
	// selected (e.g. a cross-day split item), redirect to that date first, then scroll —
	// same approach used when navigating to an appointment from a different date.
	const handleServiceItemClick = (booking) => {
		const itemId = booking?.id ?? booking?.booking_item_id;
		if (!itemId) return;

		const itemDate = getBookingItemDateObject(booking);
		if (itemDate && !isSameCalendarDay(itemDate, selectedDate)) {
			sessionStorage.setItem("skipCurrentTimeAutoScroll", "1");
			setSelectedDate(itemDate);
			setSelectedBookingId(bookingDetails?.id ?? selectedBookingId);
			setShowBookingSummary(true);
			setIsSidebarVisible(true);
			setTimeout(() => {
				scrollToBookingCard(itemId);
			}, 150);
			return;
		}

		scrollToBookingCard(itemId);
	};

	useEffect(() => {
		if (!selectedBookingId || sidebarLoading) return;
		if (!customerBookingItems || !bookingDetails) return;
		if (String(bookingDetails?.id ?? bookingDetails?.booking_id ?? "") !== String(selectedBookingId)) return;
		if (typeof window !== "undefined" && sessionStorage.getItem("skipCurrentTimeAutoScroll") === "1") return;
		if (lastScrollNavKeyRef.current === location.key) return;
		const findServiceItemId = () => {
			for (const [, bookings] of Object.entries(customerBookingItems)) {
				if (!Array.isArray(bookings)) continue;
				const match = bookings.find((booking) => {
					if (!booking) return false;
					if (Number(booking?.booking_id) === Number(selectedBookingId)) return true;
					return false;
				});
				if (match?.id != null) return match.id;
				if (match?.booking_item_id != null) return match.booking_item_id;
			}
			return null;
		};
		const targetItemId = findServiceItemId();
		if (!targetItemId) return;
		lastScrollNavKeyRef.current = location.key;
		const delay = 350;
		const timeoutId = setTimeout(() => {
			scrollToBookingCard(targetItemId);
			scrollToNewBookingCard(targetItemId);
		}, delay);
		return () => clearTimeout(timeoutId);
	}, [location.key, selectedBookingId, sidebarLoading, customerBookingItems, bookingDetails]);


	const getBaseServiceTimeDisplay = (booking) => {
		let displayTime = "";
		if (booking?.service_time) {
			displayTime = removedSeconds(booking.service_time);
		} else if (booking?.start_time) {
			const [h, m] = String(booking.start_time).split(':');
			const hour = parseInt(h, 10);
			const h12 = hour % 12 || 12;
			const ampm = hour >= 12 ? 'PM' : 'AM';
			displayTime = `${String(h12).padStart(2, '0')}:${(m || '00').slice(0, 2)} ${ampm}`;
		} else if (booking?.service_at) {
			const part = booking.service_at.split(' ')[1];
			if (part) {
				const [hh, mm] = part.split(':');
				const hour = parseInt(hh, 10);
				const h12 = hour % 12 || 12;
				const ampm = hour >= 12 ? 'PM' : 'AM';
				displayTime = `${String(h12).padStart(2, '0')}:${(mm || '00').slice(0, 2)} ${ampm}`;
			}
		}
		return displayTime;
	};

	const getServiceTimeDisplay = (booking) => {
		const displayTime = getBaseServiceTimeDisplay(booking);
		return appendExpandedTimeLabel(
			displayTime,
			booking,
			selectedOutletData?.timing,
			booking?.start_date || booking?.service_date || bookingDetails?.service_date
		);
	};

	const getBookingHeaderTimeDisplay = () => {
		const fallbackTime = appendExpandedTimeLabel(
			removedSeconds(bookingDetails?.service_time),
			bookingDetails,
			selectedOutletData?.timing,
			bookingDetails?.service_date
		);

		if (!customerBookingItems || typeof customerBookingItems !== "object" || !bookingDetails?.id) {
			return fallbackTime;
		}

		const relatedBookingItems = Object.values(customerBookingItems)
			.flatMap((bookings) => (Array.isArray(bookings) ? bookings : []))
			.filter((item) => Number(item?.booking_id) === Number(bookingDetails?.id));

		if (!relatedBookingItems.length) {
			return fallbackTime;
		}

		const bookingLevelTime = removedSeconds(bookingDetails?.service_time);
		const matchingStartItem = relatedBookingItems.find(
			(item) => getBaseServiceTimeDisplay(item) === bookingLevelTime
		);

		if (matchingStartItem) {
			return getServiceTimeDisplay(matchingStartItem);
		}

		const earliestItem = [...relatedBookingItems].sort((a, b) => {
			const aTime = new Date(String(a?.service_at || "").replace(" ", "T")).getTime();
			const bTime = new Date(String(b?.service_at || "").replace(" ", "T")).getTime();
			if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0;
			if (Number.isNaN(aTime)) return 1;
			if (Number.isNaN(bTime)) return -1;
			return aTime - bTime;
		})[0];

		return earliestItem ? getServiceTimeDisplay(earliestItem) : fallbackTime;
	};

	// Transform booking_item to group split services under parent services
	const transformCustomerData = (customerData) => {
	if (!customerData) return {};
	
	const transformedData = {};
	
	Object.entries(customerData).forEach(([customerName, bookings]) => {
		const transformedBookings = [];
		const processedBookingIds = new Set();
		
		bookings.forEach(booking => {
		const bookingId = booking.id ?? booking.booking_item_id;
		if (processedBookingIds.has(bookingId)) return;
		
		const splited = booking.splited === true || booking.splited === "true";
		
		if (splited && booking.parent_booking_item_id === null) {
			const childServices = bookings.filter(childBooking => 
			childBooking.parent_booking_item_id === bookingId && 
			(childBooking.splited === true || childBooking.splited === "true")
			);
			childServices.forEach(child => processedBookingIds.add(child.id ?? child.booking_item_id));
			transformedBookings.push({ ...booking, SplitedService: childServices });
		} else if (booking.splited === false || booking.splited === "false" || booking.parent_booking_item_id !== null) {
			const isChildOfSplitService = bookings.some(parentBooking => 
			(parentBooking.id ?? parentBooking.booking_item_id) === booking.parent_booking_item_id &&
			(parentBooking.splited === true || parentBooking.splited === "true")
			);
			if (!isChildOfSplitService) transformedBookings.push(booking);
		}
		});
		
		transformedData[customerName] = transformedBookings;
	});
	
	return transformedData;
	};

	const UpdatedCustomerBookingItems = transformCustomerData(customerBookingItems);


	const handleClick = async (action) => {
		if (isViewOnlyOutlet) {
			toast.warning("You have view-only access for this outlet.");
			return;
		}
		if (isSubmitting) return;
		setIsSubmitting(true);

		try {
		await action(); // action can be onCheckout or checkedIn
		} finally {
		setIsSubmitting(false); // re-enable after done
		}
	};

	const handleStatusWithLoading = async (status) => {
		if (isViewOnlyOutlet) {
			toast.warning("You have view-only access for this outlet.");
			return;
		}
		if (isStatusSubmitting) return;
		setIsStatusSubmitting(true);
		try {
			await handleStatus(status);
		} finally {
			setIsStatusSubmitting(false);
		}
	};

	const handleShowCancelPopup = () => {
		const parsedDeposit = Number(bookingDetails?.deposit);
		const hasDepositValue =
			bookingDetails?.deposit !== undefined &&
			bookingDetails?.deposit !== null &&
			!Number.isNaN(parsedDeposit) &&
			parsedDeposit > 0;
		if (hasDepositValue) {
			onShowCancelWithRefundPopup?.(normalizedStatus, bookingDetails?.deposit);
		} else {
			onShowCancelPopup?.(normalizedStatus, bookingDetails?.deposit);
		}
	};

	const { isBookingPaymentInProgress } = getBookingPreventChanges(bookingDetails);

	const menuOptionsMap = {
	  'confirmed': [
		{ label: "Cancel / Delete", action: handleShowCancelPopup },
		...(!repeatBooking
			? [{ label: "Make Repeating", action: showRepeatPopup }] 
			: []),
		{ label: "Check-in", action: () => handleStatusWithLoading('Check-in (In Progress)') },
		{ label: "Unconfirmed", action: () => handleStatusWithLoading('Unconfirmed') },
	  ],
	  'unconfirmed': [
		{ label: "Cancel / Delete", action: handleShowCancelPopup },
		{ label: "Confirmed", action: () => handleStatusWithLoading('Confirmed') },
	  ],
	  'check-in (in progress)': [
		{ label: "Cancel / Delete", action: handleShowCancelPopup },
		{ label: "Checkout", action: onCheckout },
		{ label: "Confirmed", action: () => handleStatusWithLoading('Confirmed'), disabled: isBookingPaymentInProgress, tooltip: isBookingPaymentInProgress ? "Payment in Progress" : "" },
	  ],
	  'checked-in': [
		{ label: "Cancel / Delete", action: handleShowCancelPopup },
		{ label: "Checkout", action: onCheckout },
		{ label: "Confirmed", action: confirmedAgainPopup },
	  ],
	  'no-show': [
		{ label: "Change Cancellation type", action: handleShowCancelPopup },
		{ label: "Confirmed", action: () => handleStatusWithLoading('Confirmed') },
	  ],
	  'completed': [
		...(!repeatBooking
			? [{ label: "Make Repeating", action: showRepeatPopup }] 
			: []),
		// { label: "Cancel / Delete", action: handleShowCancelPopup }
	  ],
	  'cancelled': [
		{ label: "Change Cancellation type", action: handleShowCancelPopup } 
	  ],
	  'voided': [
		{ label: saleNote ? "Edit Notes" : "Add Notes", action: showAddNotePopup },
	  ]
	};
	
	const menuOptions = menuOptionsMap[normalizedStatus] || [];
	const shouldHideMenuForCompletedRepeat =
		normalizedStatus === 'completed' && repeatBooking;

	const handleMenuTriggerClick = () => {
		if (isViewOnlyOutlet) {
			toast.warning("You have view-only access for this outlet.");
			return false;
		}
		return true;
	};

	if (sidebarLoading) {
		return <BookingInfoLoader/>;
	}

	return (
		<div className={`${styles.bookingInfo} h-100`}>
			<div className={styles.rightHeader}>
				<h5>Appointment <br/>
				{( bookingDetails?.id) ? (
					<i>#{ bookingDetails?.id}</i>
				) : (
					''
				)}</h5>

				{status?.toLowerCase() === 'holding' ? 
				'' 
				:
				isCustomerDeleted ?
				<div className={styles.dFlex}>
				</div>
				:
				<div className={styles.bookingInfoHeader}>
					{!isBookingPaymentInProgress && !shouldHideMenuForCompletedRepeat && (
					<div className={`${styles.menuIcon}`}>
						<DropdownImageTrigger
							iconSrc={menuIcon}
							options={menuOptions}
							menuClass={`${styles.customMenuClass} ${styles["status" + status]}`}
							onTriggerClick={handleMenuTriggerClick}
						/>
					</div>
					)}
						<ImageLink
							imageSrc={editIcon}
							altText="Link"
							handleClick={() => onEdit(status)}
							title="Edit Booking"
						/>
				</div>
				}
				
			</div>

			<div className={styles.infoWrapper}>
					<div
						className={`${styles.dFlex} ${styles.justifyBetween} ${styles.p20} ${styles.statusWrap}`}>
						<div className={`bookingStatus ${status ? `status-${status.replace(/\s+/g, "").replace(/-/g, "").replace(/[()]/g, "")}` : ""}`}>
							<p className={styles.sameFont}>
								{statusLabel}
							</p>
						</div>
						{status === 'holding' || 
						// status === 'cancelled' || 
						status === 'no-show' ||
						isCustomerDeleted
						?
						<>
						</>
						:
						<>
						{normalizedStatus === 'checked-in' || normalizedStatus === 'check-in (in progress)' ?
						<PrimaryButton
							text="Check-out"
							className={styles.checkInBtn}
							type="button"
							disabled={isSubmitting}
		  					onClick={() => handleClick(onCheckout)}
						/>
						:
						normalizedStatus === 'completed' || normalizedStatus === 'voided' ?
						<PrimaryButton
							text="View Sale"
							className={styles.checkInBtn}
							type="button"
							disabled={isViewSaleLoading}
							onClick={async () => {
								if (typeof onViewSale === "function") {
									setIsViewSaleLoading(true);
									try {
										await onViewSale();
									} finally {
										setIsViewSaleLoading(false);
									}
									return;
								}
								setCurrentStep("saleinfo");
							}}
						/>
						:
						normalizedStatus === 'confirmed' ?
						<PrimaryButton
							text="Check-in"
							className={styles.checkInBtn}
							type="button"
							disabled={isStatusSubmitting || normalizedStatus === "completed"}
							onClick={() => handleStatusWithLoading('Check-in (In Progress)')}
						/>
						:
						normalizedStatus === 'unconfirmed' ?
						<PrimaryButton
							text="Confirm"
							className={styles.checkInBtn}
							type="button"
							disabled={isStatusSubmitting}
							onClick={() => handleStatusWithLoading('Confirmed')}
						/>
						:
						<></>
						}
						</>
						}
						
					</div>

					{(normalizedStatus === 'checked-in' || normalizedStatus === 'check-in (in progress)') && 
					<div className={styles.outletName}>
						{(() => {
							// Calculate total guests and tagged guests from booking_item
							let totalGuest = 0;
							let totalGuestCheckinTagged = 0;
							
							if (customerBookingItems) {
								Object.entries(customerBookingItems).forEach(([customerName, bookings]) => {
									if (Array.isArray(bookings) && bookings.length > 0) {
										totalGuest++;
										const liveFirst = withLiveGuestTag(bookings[0]);
										if (liveFirst?.guest_checkin_id || Number(liveFirst?.below_12) === 1 || isUnder12CheckinExempt(liveFirst)) {
											totalGuestCheckinTagged++;
										}
									}
								});
							}
							
							return (
								<p className={styles.sameFont}>
									Checked in/Tagged : {totalGuestCheckinTagged}/{totalGuest}
								</p>
							);
						})()}
					</div> 
					}

					<div className={styles.halfDiv}>
						<div className="datePickerWrapper position-relative">
							<label className={styles.label}>On</label>
							<p className={styles.sameFont}>{formatDateDayMonth(bookingDetails?.service_date)}</p>
						</div>
						<div>
							<label className={styles.label}>At</label>
							<p className={styles.sameFont}>{getBookingHeaderTimeDisplay()}</p>
						</div>
					</div>

					{repeatBooking && (
						<div className={`${styles.recurringParentWrapper} d-flex`} style={{ position: 'relative' }}>
							<RepeatBookingIcon size={15} color="#3C2212"/>
							<DropdownRepeatBookingDates
								dates={repeatBookingDates}
								onSelect={handleRepeatDateClick}
								onOpen={handleRepeatDropdownOpen}
								isLoading={repeatBookingDatesLoading}
								dropdownClass={styles.recurringDropdown}
								dropdownToggleClass={styles.recurringDropdownToggle}
								dropdownMenuClass={styles.recurringDropdownMenu}
								placeholder={bookingDetails?.recurring_weeks ? "Repeats weekly" : "Repeats on selected dates"}
								emptyMessage={repeatBookingEmptyMessage}
							/>
						</div>
					)}

					{/* Primary Customer Information */}
					<div className={styles.customerInfoWrapper}>
						<div className={styles.customerCard}>
							<div className={`${styles.avatarWrapper} cursor-pointer`}
							onClick={() => {
								const contactNumber = bookingDetails?.user?.contact_number;

								const userId = bookingDetails?.user?.id;
								if (contactNumber || userId) {
									const customerData = {
										id: userId,
										name: bookingDetails?.user?.name,
										contact_number: contactNumber,
										email: bookingDetails?.user?.email,
									};

									const event = new CustomEvent('openCustomerDetails', {
										detail: { customer: customerData }
									});
									window.dispatchEvent(event);
								}
							}} 
							>
								<div className={styles.avatar} style={{backgroundColor: getConsistentColor(bookingDetails?.user?.id, bookingDetails?.user?.name)}}>
									<span>{getInitials(bookingDetails?.user?.name)}</span>
									{bookingDetails?.user?.membership === 1 && (
										<span className={styles.membershipIcon}>
											<PrimiumMemberIcon/>
										</span>
									)}
								</div>
							</div>
							<div 
								className={styles.customerDetails}>
								<div className="cursor-pointer"
								onClick={() => {
									const contactNumber = bookingDetails?.user?.contact_number;
	
									const userId = bookingDetails?.user?.id;
									if (contactNumber || userId) {
										const customerData = {
											id: userId,
											name: bookingDetails?.user?.name,
											contact_number: contactNumber,
											email: bookingDetails?.user?.email,
										};
	
										const event = new CustomEvent('openCustomerDetails', {
											detail: { customer: customerData }
										});
										window.dispatchEvent(event);
									}
								}}
								>
						    		<p className={styles.customerMobile}>
							    	{bookingDetails?.user?.contact_number}
									</p>
									<p className={styles.customerName}>
									{bookingDetails?.user?.name}
									</p>
								</div>
							


								{customerCheckedInData?.tagLabel ? (
									<div className={`${styles.guestCheckinTagContainer} ${styles.tagSuccess}`}>
										{customerCheckedInData.tagLabel}
									</div>
								) : (
									<div className={styles.guestCheckinTagContainer} title="Click edit to tag">
										Not Tagged
									</div>
								)}

								{bookingDetails?.user?.note?.trim() && (
									<div className={styles.customerNotes}>
										<p className={styles.customerNotesText}>
											{bookingDetails?.user?.note}
										</p>
									</div>
								)}
								{isCustomerDeleted && (
									<div className={styles.customerDeleted}>
										<p className={styles.customerDeletedText}>Customer Deleted</p>
									</div>
								)}
							</div>
						</div>
					</div>


					<div className={styles.servicesInfoWrapper}>
						{(() => {
							// Separate primary customer and guest data
							const primaryCustomerData = [];
							const guestData = [];

							if (UpdatedCustomerBookingItems) {
								Object.entries(UpdatedCustomerBookingItems).forEach(([customerName, bookings]) => {
									if (isPrimaryCustomerGroup(customerName, bookings, bookingDetails?.user?.name)) {
										primaryCustomerData.push({ customerName, bookings });
									} else {
										guestData.push({ customerName, bookings });
									}
								});
							}

							return (
								<>
									{/* Primary Customer Data - Show first without guest icon and name */}
									{primaryCustomerData.map(({ customerName, bookings }) => (
										<div key={`primary-${customerName}`} className={styles.paxBlock}>
											{bookings.map((booking, index) => {
												return (
													<div key={index} className={styles.serviceLine}>
														<div className={styles.serviceDetails}
													   onClick={() => handleServiceItemClick(booking)}
													>
		
													<div className={`${styles.selectedServiceHead} ${styles.spacing}`}>
														<h3>
															{`
															${booking?.duration ?? ''} Mins 
															${booking?.service ?? booking?.service_name ?? ''}
															`}
														</h3>
													</div>

													{booking?.addons?.length > 0 && (
														<div className={`${styles.dFlex} ${styles.spacing}`}>
															<label className={styles.label}>Add-on:</label>
															<p className={styles.sameFont}>
																{booking.addons.map((addon) => addon.name).join(', ')}
															</p>
														</div>
													)}

														<div className={`${styles.selectedTherapistCard} ${styles.spacing} d-flex justify-between`}>
															<div className={`${styles.dFlex}`}>
																<label className={styles.label}>With:</label>
																<span
																	className={`${styles.therapistCircle} ${
																	(booking?.gender ?? booking?.therapist_gender) === "female" ? styles.female : 
																	(booking?.gender ?? booking?.therapist_gender) === "male" ? styles.male : ''
																	}`}
																	title={booking?.therapist_pager_number ?? 'N/A'}
																>
																	{booking?.therapist_pager_number ?? 'N/A'}
																</span>
																<p className={styles.sameFont} title={(booking?.therapist ?? booking?.therapist_name) ?? 'N/A'}>{booking?.therapist ?? booking?.therapist_name}</p>
															</div>
															{booking?.requested_person === 1 && <RequestedTherapist/>}
														</div>

									

														{Array.isArray(booking?.room_items) && booking.room_items.length > 0 && (
															<div className={`${styles.spacing} d-flex align-center justify-between`}>
																<div className={styles.dFlex}>
																	<label className={styles.label}>Using:</label>
																	{booking.room_items.map((entry, idx) => {
																		const item = entry?.rm || entry;
																		const display = item?.room_name || item?.item_type || "";
																		return (
																			<p key={idx} className={styles.sameFont}>{display}</p>
																		);
																	})}
																</div>
																{booking?.requested_room === 1 && <RequestedRoom/>}
															</div>
														)}
														<div className={`${styles.dFlex} ${styles.spacing}`}>
															<label className={styles.label}>Request:</label>
															<p className={styles.sameFont}>
																{booking?.service_request ? booking.service_request : "None"}
															</p>
														</div>

														<div className={`${styles.dFlex} ${styles.spacing}`}>
															<div className={`${styles.dFlex}`}>
																<label className={styles.label}>For:</label>
																<p className={styles.sameFont}>{booking?.duration ?? booking?.duration ?? ''} mins</p>
															</div>
															<div className={`${styles.dFlex} ${styles.space}`}>
																<label className={styles.label}>At:</label>
																<p className={styles.sameFont}>{getServiceTimeDisplay(booking)}</p>
															</div>
														</div>

														{/* SPLITED SERVICE  START */}
														{booking?.SplitedService?.length > 0 && (
															<div className={styles.splitedService}>
															{booking?.SplitedService?.map((splitedService) => {
																return (
																<div key={splitedService?.id ?? splitedService?.booking_item_id}>
																<div className={`${styles.selectedTherapistCard} d-flex justify-between`}>
																	<div className={`${styles.dFlex}`}>
																	<label className={styles.label}>With:</label>
																	<span
																		className={`${styles.therapistCircle} ${
																		(splitedService?.gender ?? splitedService?.therapist_gender) === "female" ? styles.female : 
																		(splitedService?.gender ?? splitedService?.therapist_gender) === "male" ? styles.male : ''
																		}`}
																	>
																		{splitedService?.therapist_pager_number ?? 'N/A'}
																	</span>
																	<p className={styles.sameFont}>{splitedService?.therapist ?? splitedService?.therapist_name}</p>
																	</div>
																	{splitedService?.requested_person === 1 && <RequestedTherapist/>}
																</div>

																<div className={`${styles.dFlex} ${styles.spacing}`}>
																	<div className={`${styles.dFlex}`}>
																	<label className={styles.label}>For:</label>
																	<p className={styles.sameFont}>{splitedService?.duration ?? splitedService?.duration ?? ''} mins</p>
																	</div>
																	<div className={`${styles.dFlex} ${styles.space}`}>
																	<label className={styles.label}>At:</label>
																	<p className={styles.sameFont}>{getServiceTimeDisplay(splitedService)}</p>
																	</div>
																</div>
																</div>
																);
															})}
															</div>
														)}
														{/* SPLITED SERVICE  END */}
														
													</div>
													</div>
													);
												})}
										</div>
									))}
									
									{/* Guest Data - Show after primary customer data with guest icon and name */}
									{guestData.map(({ customerName, bookings }) => (	
										<div key={`guest-${customerName}`} className={styles.paxBlock}>
											<div className={`${styles.paxHeader}`}>
												<div className={`${styles.dFlex}`}>
													<img src={Guesticon} alt="guestIcon" />
													<p className={styles.paxName}>{customerName}</p>
												</div>
											{bookings?.[0] && (
												getGuestCheckinTagLabel(withLiveGuestTag(bookings[0])) ? (
													<div className={`${styles.guestCheckinTagContainer} ${styles.paxTag} ${styles.tagSuccess}`}>
													{getGuestCheckinTagLabel(withLiveGuestTag(bookings[0]))}
													</div>
												) : (
													<div className={`${styles.guestCheckinTagContainer} ${styles.paxTag}`} title="Click edit to tag">
													Not Tagged
													</div>
												)
												)}
											</div>
											{bookings.map((booking, index) => {
												return (
													<div key={index} className={styles.serviceLine}>
														<div className={styles.serviceDetails}
													   onClick={() => handleServiceItemClick(booking)}
													>
														<div className={`${styles.selectedServiceHead} ${styles.spacing}`}>
															<h3>
																{`
																${booking?.duration ?? ''} Mins 
																${booking?.service ?? booking?.service_name ?? ''}
																`}
															</h3>
														</div>

														{booking?.addons?.length > 0 && (
															<div className={`${styles.dFlex} ${styles.spacing}`}>
																<label className={styles.label}>Add-on:</label>
																<p className={styles.sameFont}>
																	{booking.addons.map((addon) => addon.name).join(', ')}
																</p>
															</div>
														)}


														<div className={`${styles.selectedTherapistCard} ${styles.spacing} d-flex justify-between`}>
															<div className={`${styles.dFlex}`}>
																<label className={styles.label}>With:</label>
																<span
																	className={`${styles.therapistCircle} ${
																	(booking?.gender ?? booking?.therapist_gender) === "female" ? styles.female : 
																	(booking?.gender ?? booking?.therapist_gender) === "male" ? styles.male : ''
																	}`}
																	title={booking?.therapist_pager_number ?? 'N/A'}
																>
																	{booking?.therapist_pager_number ?? 'N/A'}
																</span>
																<p className={styles.sameFont} title={(booking?.therapist ?? booking?.therapist_name) ?? 'N/A'}>{booking?.therapist ?? booking?.therapist_name}</p>
															</div>
															{booking?.requested_person === 1 && <RequestedTherapist/>}
														</div>
														

														{Array.isArray(booking?.room_items) && booking.room_items.length > 0 && (
															<div className={`${styles.spacing} d-flex align-center justify-between`}>
																<div className={styles.dFlex}>
																	<label className={styles.label}>Using:</label>
																	{booking.room_items.map((entry, idx) => {
																		const item = entry?.rm || entry;
																		const display = item?.room_name || item?.item_type || "";
																		return (
																			<p key={idx} className={styles.sameFont}>{display}</p>
																		);
																	})}
																</div>
																{booking?.requested_room === 1 && <RequestedRoom/>}
															</div>
														)}
														
														<div className={`${styles.dFlex} ${styles.spacing}`}>
															<label className={styles.label}>Request:</label>
															<p className={styles.sameFont}>
																{booking?.service_request ? booking.service_request : "None"}
															</p>
														</div>

														<div className={`${styles.dFlex} ${styles.spacing}`}>
															<div className={`${styles.dFlex}`}>
																<label className={styles.label}>For:</label>
																<p className={styles.sameFont}>{booking?.duration ?? booking?.duration ?? ''} mins</p>
															</div>
															<div className={`${styles.dFlex} ${styles.space}`}>
																<label className={styles.label}>At:</label>
																<p className={styles.sameFont}>{getServiceTimeDisplay(booking)}</p>
															</div>
														</div>

														{/* SPLITED SERVICE  START */}
														{booking?.SplitedService?.length > 0 && (
															<div className={styles.splitedService}>
															{booking?.SplitedService?.map((splitedService) => {
																return (
																<div key={splitedService?.id ?? splitedService?.booking_item_id}>
																<div className={`${styles.selectedTherapistCard} d-flex justify-between`}>
																	<div className={`${styles.dFlex}`}>
																	<label className={styles.label}>With:</label>
																	<span
																		className={`${styles.therapistCircle} ${
																		(splitedService?.gender ?? splitedService?.therapist_gender) === "female" ? styles.female : 
																		(splitedService?.gender ?? splitedService?.therapist_gender) === "male" ? styles.male : ''
																		}`}
																	>
																		{splitedService?.therapist_pager_number ?? 'N/A'}
																	</span>
																	<p className={styles.sameFont}>{splitedService?.therapist ?? splitedService?.therapist_name}</p>
																	</div>
																	{splitedService?.requested_person === 1 && <RequestedTherapist/>}
																</div>

																<div className={`${styles.dFlex} ${styles.spacing}`}>
																	<div className={`${styles.dFlex}`}>
																	<label className={styles.label}>For:</label>
																	<p className={styles.sameFont}>{splitedService?.duration ?? splitedService?.duration ?? ''} mins</p>
																	</div>
																	<div className={`${styles.dFlex} ${styles.space}`}>
																	<label className={styles.label}>At:</label>
																	<p className={styles.sameFont}>{getServiceTimeDisplay(splitedService)}</p>
																	</div>
																</div>
																</div>
																);
															})}
															</div>
														)}
														{/* SPLITED SERVICE  END */}
														</div>
													</div>
													);
												})}
										</div>
									))}
								</>
							);
						})()}
					</div>


					{/* NOTES START */}
					{(bookingDetails?.note || bookingDetails?.web_notes?.trim()) && (
						<BookingNotes
							outletNotes={bookingDetails?.note}
							webCustomerNotes={bookingDetails?.web_notes}
						/>
					)}
					{/* NOTES END */}

					<BookingDetails
						heading="Booking Details"
						CreateDate={bookingDetails?.booking_created_at ?? bookingDetails?.created_at}
						CreatedBy={bookingDetails?.created_by_name}
						UpdatedDate={bookingDetails?.updated_at}
						LastUpdatedBy={bookingDetails?.updated_by_name}
						BookingDate={bookingDetails?.booking_date}
						Source={bookingDetails?.source}
						rescheduleCount={bookingDetails?.reschedule}
					/>

				</div>
			
		</div>
	);
};

export default BookingInfo;
